import imageCompression from 'browser-image-compression';

const COMPRESSION_OPTIONS = {
  maxSizeMB: 1,
  maxWidthOrHeight: 2000,
  useWebWorker: true,
  fileType: 'image/jpeg' as const,
  initialQuality: 0.85,
};

// File types that should NOT be compressed
const SKIP_COMPRESSION_TYPES = [
  'image/gif',
  'image/svg+xml',
];

export interface CompressionResult {
  file: File;
  originalSize: number;
  compressedSize: number;
  saved: number;
  wasCompressed: boolean;
}

export async function compressImage(file: File): Promise<CompressionResult> {
  const originalSize = file.size;
  
  // Skip compression for GIFs and SVGs
  if (SKIP_COMPRESSION_TYPES.includes(file.type)) {
    return {
      file,
      originalSize,
      compressedSize: originalSize,
      saved: 0,
      wasCompressed: false,
    };
  }
  
  // Skip small files (under 100KB)
  if (file.size < 100 * 1024) {
    return {
      file,
      originalSize,
      compressedSize: originalSize,
      saved: 0,
      wasCompressed: false,
    };
  }
  
  try {
    // Check if file has transparency (PNG with alpha)
    const hasTransparency = file.type === 'image/png';
    
    const options = {
      ...COMPRESSION_OPTIONS,
      // Keep PNG format if it might have transparency
      fileType: hasTransparency ? ('image/png' as const) : ('image/jpeg' as const),
    };
    
    const compressedFile = await imageCompression(file, options);
    const compressedSize = compressedFile.size;
    
    // Only use compressed version if it's actually smaller
    if (compressedSize < originalSize) {
      return {
        file: compressedFile,
        originalSize,
        compressedSize,
        saved: originalSize - compressedSize,
        wasCompressed: true,
      };
    }
    
    return {
      file,
      originalSize,
      compressedSize: originalSize,
      saved: 0,
      wasCompressed: false,
    };
  } catch (error) {
    console.error('Image compression failed:', error);
    // Return original file if compression fails
    return {
      file,
      originalSize,
      compressedSize: originalSize,
      saved: 0,
      wasCompressed: false,
    };
  }
}

export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}
